/**
 * Package Manager — visual npm dependency manager.
 *
 * Lets you add/remove npm packages without typing in the terminal. Reads
 * package.json from the WebContainer filesystem (source of truth after install),
 * shows current deps, and runs npm install/uninstall when you add or remove.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Package, Plus, Trash2, Search, Loader2, RefreshCw, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { getWebContainer } from '../../services/webcontainer';

interface PackageManagerProps {
  packageJson: any;
  onPackageJsonChange: (newPackageJson: any) => void;
}

// eslint-disable-next-line no-control-regex
const stripAnsi = (s: string) => s.replace(/\x1b\[[0-9;]*[A-Za-z]/g, '');

const PackageManager: React.FC<PackageManagerProps> = ({ packageJson, onPackageJsonChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDev, setIsDev] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [installOutput, setInstallOutput] = useState<string>('');
  const [dependencies, setDependencies] = useState<Record<string, string>>({});
  const [devDependencies, setDevDependencies] = useState<Record<string, string>>({});
  const [depsExpanded, setDepsExpanded] = useState(true);
  const [devDepsExpanded, setDevDepsExpanded] = useState(true);

  useEffect(() => {
    if (packageJson) {
      setDependencies(packageJson.dependencies || {});
      setDevDependencies(packageJson.devDependencies || {});
    }
  }, [packageJson]);

  // Auto-refresh from WebContainer FS whenever the panel mounts/becomes visible.
  // This syncs deps installed via the Terminal (which bypass React state).
  useEffect(() => {
    refreshFromContainer();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshFromContainer = useCallback(async () => {
    try {
      const wc = await getWebContainer();
      const raw = await wc.fs.readFile('package.json', 'utf-8');
      const text = typeof raw === 'string' ? raw : new TextDecoder().decode(raw);
      const pkg = JSON.parse(text);
      setDependencies(pkg.dependencies || {});
      setDevDependencies(pkg.devDependencies || {});
      onPackageJsonChange(pkg);
    } catch (e) {
      console.error('PackageManager: failed to refresh from container FS:', e);
    }
  }, [onPackageJsonChange]);

  const readOutput = async (proc: any, onChunk: (t: string) => void) => {
    const reader = proc.output.getReader();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      onChunk(typeof value === 'string' ? value : new TextDecoder().decode(value));
    }
  };

  const handleInstall = async (name?: string) => {
    const pkg = name?.trim();
    const label = pkg || 'all dependencies';
    setInstalling(true);
    setInstallOutput(`Installing ${label}…\n`);

    try {
      const wc = await getWebContainer();
      const args = ['install'];
      if (pkg) {
        args.push(pkg, isDev ? '--save-dev' : '--save');
      }
      args.push('--no-progress', '--loglevel=error');

      const proc = await wc.spawn('npm', args);
      await readOutput(proc, (t) => setInstallOutput((prev) => prev + t));
      const code = await proc.exit;

      if (code === 0) {
        setInstallOutput((prev) => prev + `\nDone\n`);
        await refreshFromContainer();
        if (pkg) setSearchTerm('');
      } else {
        setInstallOutput((prev) => prev + `\nFailed (exit ${code})\n`);
      }
    } catch (e: any) {
      setInstallOutput((prev) => prev + `\n${e.message}\n`);
    } finally {
      setInstalling(false);
    }
  };

  const handleRemove = async (name: string) => {
    setInstalling(true);
    setInstallOutput(`Removing ${name}…\n`);
    try {
      const wc = await getWebContainer();
      const proc = await wc.spawn('npm', ['uninstall', name]);
      await readOutput(proc, (t) => setInstallOutput((prev) => prev + t));
      await proc.exit;
      setInstallOutput((prev) => prev + `Removed\n`);
      await refreshFromContainer();
    } catch (e: any) {
      setInstallOutput((prev) => prev + `${e.message}\n`);
    } finally {
      setInstalling(false);
    }
  };

  const depEntries = Object.entries(dependencies);
  const devEntries = Object.entries(devDependencies);

  // Determine output status for styling
  const outputStatus = installOutput.includes('Done') || installOutput.includes('Removed')
    ? 'success'
    : installOutput.includes('Failed') || installOutput.includes('Error')
      ? 'error'
      : 'running';

  return (
    <div className="flex flex-col gap-3 h-full">
      {/* Search + Install bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && searchTerm.trim()) handleInstall(searchTerm.trim()); }}
            placeholder="Add package…"
            disabled={installing}
            className="w-full pl-8 pr-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500/40 text-gray-900 dark:text-white placeholder-gray-400 transition-shadow"
          />
        </div>
        <button
          onClick={() => handleInstall(searchTerm.trim())}
          disabled={!searchTerm.trim() || installing}
          title="Install package"
          className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary-600 hover:bg-primary-500 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          {installing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-4 h-4" />}
        </button>
      </div>

      {/* Options row */}
      <div className="flex items-center justify-between">
        <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isDev}
            onChange={(e) => setIsDev(e.target.checked)}
            className="w-3 h-3 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500/40 bg-transparent"
          />
          <span className="text-[11px] text-gray-600 dark:text-gray-400">devDependency</span>
        </label>

        <button
          onClick={() => handleInstall()}
          disabled={installing}
          title="Run npm install"
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-600 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
        >
          <Download className="w-3 h-3" />
          npm install
        </button>
      </div>

      {/* Dependency lists */}
      <div className="flex-1 overflow-y-auto space-y-1 min-h-0">
        {/* Dependencies section */}
        <div>
          <button
            onClick={() => setDepsExpanded(!depsExpanded)}
            className="flex items-center gap-1.5 w-full py-1.5 text-left group"
          >
            {depsExpanded
              ? <ChevronDown className="w-3 h-3 text-gray-400" />
              : <ChevronRight className="w-3 h-3 text-gray-400" />
            }
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
              dependencies
            </span>
            <span className="text-[10px] text-gray-500 dark:text-gray-500 ml-auto tabular-nums">
              {depEntries.length}
            </span>
          </button>

          {depsExpanded && (
            <div className="space-y-px ml-1">
              {depEntries.length === 0 ? (
                <p className="text-[11px] text-gray-500 dark:text-gray-500 py-2 pl-4 italic">No dependencies</p>
              ) : (
                depEntries.map(([name, version]) => (
                  <div
                    key={name}
                    className="group flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
                  >
                    <Package className="w-3 h-3 text-gray-500 dark:text-gray-500 shrink-0" />
                    <span className="text-[13px] font-medium text-gray-800 dark:text-gray-200 truncate flex-1 min-w-0">
                      {name}
                    </span>
                    <span className="text-[11px] text-gray-500 dark:text-gray-500 font-mono tabular-nums shrink-0">
                      {version as string}
                    </span>
                    <button
                      onClick={() => handleRemove(name)}
                      disabled={installing}
                      className="p-0.5 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 hover:text-rose-500 dark:hover:text-rose-400 disabled:opacity-30 transition-all"
                      title="Uninstall"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Dev Dependencies section */}
        <div>
          <button
            onClick={() => setDevDepsExpanded(!devDepsExpanded)}
            className="flex items-center gap-1.5 w-full py-1.5 text-left group"
          >
            {devDepsExpanded
              ? <ChevronDown className="w-3 h-3 text-gray-400" />
              : <ChevronRight className="w-3 h-3 text-gray-400" />
            }
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
              devDependencies
            </span>
            <span className="text-[10px] text-gray-500 dark:text-gray-500 ml-auto tabular-nums">
              {devEntries.length}
            </span>
          </button>

          {devDepsExpanded && (
            <div className="space-y-px ml-1">
              {devEntries.length === 0 ? (
                <p className="text-[11px] text-gray-500 dark:text-gray-500 py-2 pl-4 italic">No dev dependencies</p>
              ) : (
                devEntries.map(([name, version]) => (
                  <div
                    key={name}
                    className="group flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
                  >
                    <Package className="w-3 h-3 text-gray-500 dark:text-gray-500 shrink-0" />
                    <span className="text-[13px] font-medium text-gray-800 dark:text-gray-200 truncate flex-1 min-w-0">
                      {name}
                    </span>
                    <span className="text-[11px] text-gray-500 dark:text-gray-500 font-mono tabular-nums shrink-0">
                      {version as string}
                    </span>
                    <button
                      onClick={() => handleRemove(name)}
                      disabled={installing}
                      className="p-0.5 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 hover:text-rose-500 dark:hover:text-rose-400 disabled:opacity-30 transition-all"
                      title="Uninstall"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Install output — inline log */}
      {installOutput && (
        <div className={`rounded-lg border overflow-hidden transition-colors ${outputStatus === 'success'
          ? 'border-emerald-500/20 bg-emerald-950/20'
          : outputStatus === 'error'
            ? 'border-rose-500/20 bg-rose-950/20'
            : 'border-gray-700/50 bg-gray-900'
          }`}>
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-800/50">
            <div className="flex items-center gap-2">
              {installing ? (
                <Loader2 className="w-3 h-3 text-primary-400 animate-spin" />
              ) : outputStatus === 'success' ? (
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
              ) : (
                <div className="w-2 h-2 rounded-full bg-rose-400" />
              )}
              <span className="text-[11px] font-medium text-gray-400">
                {installing ? 'Running…' : outputStatus === 'success' ? 'Complete' : 'Error'}
              </span>
            </div>
            {!installing && (
              <button
                onClick={() => setInstallOutput('')}
                className="text-[10px] text-gray-500 hover:text-gray-300 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
          <div className="px-3 py-2 max-h-28 overflow-y-auto">
            <pre className="text-[11px] font-mono text-gray-300 whitespace-pre-wrap leading-relaxed">{stripAnsi(installOutput)}</pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageManager;
